import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chatService } from '../../shared/services/chat.service';
import type { ChatMessage, ConversationDetail } from '../../shared/services/chat.service';

const DETAIL_KEY = (id: string) => ['chat', 'conversation', id] as const;
const MESSAGES_KEY = (id: string) => ['chat', 'messages', id] as const;
const INBOX_KEY = ['chat', 'conversations'] as const;

/** How often to poll messages while the chat is open. Replaced by a STOMP
 *  subscription in phase 2. */
const POLL_INTERVAL_MS = 4000;

/**
 * Hook for a single open chat. Loads the header + message history and polls
 * for new messages on a short interval. Marking-as-read fires once on mount.
 */
export function useConversation(conversationId: string | null) {
  const qc = useQueryClient();

  const detailQuery = useQuery<ConversationDetail>({
    queryKey: DETAIL_KEY(conversationId ?? ''),
    queryFn: async () => (await chatService.getDetail(conversationId!)).data,
    enabled: Boolean(conversationId),
  });

  const messagesQuery = useQuery<ChatMessage[]>({
    queryKey: MESSAGES_KEY(conversationId ?? ''),
    queryFn: async () => (await chatService.listMessages(conversationId!, { size: 100 })).data,
    enabled: Boolean(conversationId),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  // Mark inbound messages read when the chat opens. The inbox unread count
  // depends on this so we invalidate it afterwards.
  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    chatService
      .markRead(conversationId)
      .then(() => {
        if (cancelled) return;
        qc.invalidateQueries({ queryKey: INBOX_KEY });
        qc.invalidateQueries({ queryKey: DETAIL_KEY(conversationId) });
        // Drop the header unread-messages badge straight away.
        qc.invalidateQueries({ queryKey: ['conversations', 'unread-count'] });
      })
      .catch(() => {
        // Best-effort — failures here just mean the inbox badge stays bumped.
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, qc]);

  const send = useMutation({
    mutationFn: (body: string) => chatService.sendMessage(conversationId!, body),
    onSuccess: (msg) => {
      qc.setQueryData<ChatMessage[]>(MESSAGES_KEY(conversationId!), (cur) => {
        if (!cur) return [msg];
        // Avoid duplicates if the polling round-tripped first.
        if (cur.some((m) => m.id === msg.id)) return cur;
        return [...cur, msg];
      });
      qc.invalidateQueries({ queryKey: INBOX_KEY });
    },
  });

  return {
    detail: detailQuery.data ?? null,
    messages: messagesQuery.data ?? [],
    isLoading: detailQuery.isLoading || messagesQuery.isLoading,
    isError: detailQuery.isError || messagesQuery.isError,
    sendMessage: (body: string) => send.mutateAsync(body),
    isSending: send.isPending,
  };
}

/** Hook for the inbox page — list of every conversation the caller is in. */
export function useConversations(options?: { pollMs?: number }) {
  const pollMs = options?.pollMs ?? 8000;
  const query = useQuery({
    queryKey: INBOX_KEY,
    queryFn: async () => (await chatService.listMine()).data,
    refetchInterval: pollMs,
    refetchIntervalInBackground: false,
  });
  return {
    conversations: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
